/**
 * Buchhaltungsexport im DATEV-Format „Buchungsstapel“ (EXTF, Version 700, Kategorie 21) für den Steuerberater. Rein; die
 * Oberfläche kodiert die Datei als Windows-1252 (winAnsiFileBytes) und lädt sie herunter.
 *
 * Gebucht werden die Belege der Agentur: Ausgangsrechnungen an Marken und Creator (Debitor an Erlöskonto) und Rechnungen bzw.
 * Gutschriften der Creator an die Agentur (Aufwand an Kreditor). Rechnungen eines Creators direkt an eine Marke sind keine
 * Belege der Agentur und fehlen. Stornorechnungen haben negative Beträge und buchen mit umgekehrtem Soll/Haben.
 * Die Standardkonten folgen SKR03 bzw. SKR04 und sind je Agentur einstellbar: vor dem ersten Import mit dem Steuerberater abstimmen.
 *
 * 0055 (Befund 1, 22, 24): Creator-Rechnungen an die Agentur zählen erst ab der Freigabe (eingereicht, zurückgegeben, abgelehnt:
 * kein Beleg der Agentur). Wird eine freigegebene Creator-Rechnung bzw. Gutschrift später storniert (Widerspruch), entsteht im
 * Zeitraum der Stornierung eine Gegenbuchung (Generalumkehr) statt einer zweiten positiven Buchung. Forderungsausfälle buchen
 * den ausgebuchten Betrag auf „Forderungsverluste“ (Automatikkonto mit Umsatzsteuerkorrektur). Eine Agentur als
 * Kleinunternehmer zieht keine Vorsteuer (kein BU-Schlüssel auf Creator-Rechnungen).
 *
 * K-308 (0274): Hat eine Rechnung in fremder Währung die beim Festschreiben eingefrorene Umsatzsteuer in Euro (0267/0269, auf dem
 * PDF gedruckt), rechnet der Stapel die ganze Rechnung mit deren Kurs um: Basisumsatz = Netto in Euro + Umsatzsteuer in Euro wie
 * auf dem PDF, Kurs = der gedruckte Kurs; Betrag, Kurs, Tag und Quelle stehen zusätzlich in Beleginfo 1. Ohne diese Angaben gilt
 * der Euro-Betrag beim Versand (amount_org_cents). Verzugskosten (Steuerfall NOT_TAXABLE_DAMAGES) sind kein Umsatzerlös und buchen
 * auf ein eigenes Konto (revenue_late_costs, K-313), wie im BMD-Export.
 *
 * K-397: Die DATEV-Automatik (Erlöse 19 %/7 %, Vorsteuer über BU 9/8, Forderungsverluste 19 %/7 %) zieht die Umsatzsteuer aus dem
 * Euro-Brutto (Basisumsatz): USt = Brutto − Brutto/(1 + Satz), kaufmännisch gerundet. Netto in Euro plus USt in Euro (PDF) ergab
 * deshalb in rund 13–20 % (19 %) bzw. 19–26 % (7 %) der Fälle 1 Cent Abweichung. Der Basisumsatz wird jetzt so gewählt, dass die
 * Automatik genau die USt in Euro des PDF (invoices.tax_eur_cents) zieht, und zwar der passende Betrag, der dem rechnerischen
 * Brutto am nächsten liegt (datevBasisForTax). Für 19 % und 7 % gibt es immer einen: zu jedem Cent Steuer gehören 6–7 (19 %) bzw.
 * 15–16 (7 %) aufeinanderfolgende Bruttobeträge. Nur wenn kein Betrag passt (nicht ganzzahlige Sätze, nie bei DATEV-Automatik),
 * bleibt das nächstliegende Brutto mit der kleinsten Abweichung stehen (exact = false). Alle Rundungen sind symmetrisch zum
 * Nullpunkt (roundHalfAway, wie PostgreSQL round()), damit ein Storno die Originalbuchung spiegelgleich aufhebt.
 *
 * K-399: Verzugszinsen sind nach § 275 Abs. 2 Nr. 11 HGB Zinserträge (SKR03 2650, SKR04 7100), Mahngebühr und Verzugspauschale
 * bleiben Schadensersatz auf dem Verzugskostenkonto (2700/4830). Ein Beleg über Verzugskosten (costs_for, 0198) bucht deshalb je
 * Position: die Zinsen (lateInterestCents aus export_invoices, 0284) auf revenue_late_interest, der Rest auf revenue_late_costs.
 */
import { csvFormulaGuard } from "./csv.js";
import { taxModeFromDb, type TaxMode } from "./tax.js";
import type { Cents } from "./types.js";

/** Spaltenkopf des Buchungsstapels (Zeile 2), in dieser Reihenfolge */
export const DATEV_COLUMNS: readonly string[] = ["Umsatz (ohne Soll/Haben-Kz)", "Soll/Haben-Kennzeichen", "WKZ Umsatz", "Kurs", "Basisumsatz", "WKZ Basisumsatz", "Konto", "Gegenkonto (ohne BU-Schlüssel)", "BU-Schlüssel", "Belegdatum", "Belegfeld 1", "Belegfeld 2", "Skonto", "Buchungstext", "Postensperre", "Diverse Adressnummer", "Geschäftspartnerbank", "Sachverhalt", "Zinssperre", "Beleglink", "Beleginfo – Art 1", "Beleginfo – Inhalt 1", "Beleginfo – Art 2", "Beleginfo – Inhalt 2", "Beleginfo – Art 3", "Beleginfo – Inhalt 3", "Beleginfo – Art 4", "Beleginfo – Inhalt 4", "Beleginfo – Art 5", "Beleginfo – Inhalt 5", "Beleginfo – Art 6", "Beleginfo – Inhalt 6", "Beleginfo – Art 7", "Beleginfo – Inhalt 7", "Beleginfo – Art 8", "Beleginfo – Inhalt 8", "KOST1 – Kostenstelle", "KOST2 – Kostenstelle", "Kost Menge", "EU-Land u. USt-IdNr.", "EU-Steuersatz", "Abw. Versteuerungsart", "Sachverhalt L+L", "Funktionsergänzung L+L", "BU 49 Hauptfunktionstyp", "BU 49 Hauptfunktionsnummer", "BU 49 Funktionsergänzung", "Zusatzinformation – Art 1", "Zusatzinformation – Inhalt 1", "Zusatzinformation – Art 2", "Zusatzinformation – Inhalt 2", "Zusatzinformation – Art 3", "Zusatzinformation – Inhalt 3", "Zusatzinformation – Art 4", "Zusatzinformation – Inhalt 4", "Zusatzinformation – Art 5", "Zusatzinformation – Inhalt 5", "Zusatzinformation – Art 6", "Zusatzinformation – Inhalt 6", "Zusatzinformation – Art 7", "Zusatzinformation – Inhalt 7", "Zusatzinformation – Art 8", "Zusatzinformation – Inhalt 8", "Zusatzinformation – Art 9", "Zusatzinformation – Inhalt 9", "Zusatzinformation – Art 10", "Zusatzinformation – Inhalt 10", "Zusatzinformation – Art 11", "Zusatzinformation – Inhalt 11", "Zusatzinformation – Art 12", "Zusatzinformation – Inhalt 12", "Zusatzinformation – Art 13", "Zusatzinformation – Inhalt 13", "Zusatzinformation – Art 14", "Zusatzinformation – Inhalt 14", "Zusatzinformation – Art 15", "Zusatzinformation – Inhalt 15", "Zusatzinformation – Art 16", "Zusatzinformation – Inhalt 16", "Zusatzinformation – Art 17", "Zusatzinformation – Inhalt 17", "Zusatzinformation – Art 18", "Zusatzinformation – Inhalt 18", "Zusatzinformation – Art 19", "Zusatzinformation – Inhalt 19", "Zusatzinformation – Art 20", "Zusatzinformation – Inhalt 20", "Stück", "Gewicht", "Zahlweise", "Forderungsart", "Veranlagungsjahr", "Zugeordnete Fälligkeit", "Skontotyp", "Auftragsnummer", "Buchungstyp", "USt-Schlüssel (Anzahlungen)", "EU-Mitgliedstaat (Anzahlungen)", "Sachverhalt L+L (Anzahlungen)", "EU-Steuersatz (Anzahlungen)", "Erlöskonto (Anzahlungen)", "Herkunft-Kz", "Leerfeld", "KOST-Datum", "SEPA-Mandatsreferenz", "Skontosperre", "Gesellschaftername", "Beteiligtennummer", "Identifikationsnummer", "Zeichnernummer", "Postensperre bis", "Bezeichnung", "Kennzeichen", "Festschreibung", "Leistungsdatum", "Datum Zuord.", "Fälligkeit", "Generalumkehr", "Steuersatz", "Land", "Abrechnungsreferent", "BVV-Position", "EU-Mitgliedstaat u. UStID (Ursprung)", "EU-Steuersatz (Ursprung)", "Abw. Skontokonto"];

export type DatevChart = "SKR03" | "SKR04";
export type DatevAccountKey =
  | "debtor" | "creditor"
  | "revenue_19" | "revenue_7" | "revenue_eu_rc" | "revenue_13b" | "revenue_abroad" | "revenue_small_business" | "revenue_other"
  | "revenue_late_costs" | "revenue_late_interest"
  | "expense" | "expense_eu_rc" | "expense_abroad"
  | "bad_debt_19" | "bad_debt_7" | "bad_debt_other";

/**
 * K-313: Verzugskosten (Verzugspauschale § 288 Abs. 5 BGB, Mahnkosten, Verzugszinsen) als nicht steuerbarer Schadensersatz:
 * „Sonstige Erträge“ ohne Umsatzsteuer-Automatik (SKR03 2700, SKR04 4830) statt der Umsatzerlöse. Seit K-399 buchen die
 * Verzugszinsen eines Belegs getrennt auf DATEV_DEFAULT_LATE_INTEREST_ACCOUNT.
 */
export const DATEV_DEFAULT_LATE_COSTS_ACCOUNT: Readonly<Record<"SKR03" | "SKR04", number>> = { SKR03: 2700, SKR04: 4830 };
/** K-399: Verzugszinsen als Zinsertrag (§ 275 Abs. 2 Nr. 11 HGB): „Sonstige Zinsen und ähnliche Erträge“ SKR03 2650, SKR04 7100 */
export const DATEV_DEFAULT_LATE_INTEREST_ACCOUNT: Readonly<Record<"SKR03" | "SKR04", number>> = { SKR03: 2650, SKR04: 7100 };

/** Standardkonten je Kontenrahmen (Sammeldebitor/-kreditor, Automatikkonten für Erlöse, Fremdleistungen) */
export const DATEV_DEFAULT_ACCOUNTS: Record<DatevChart, Record<DatevAccountKey, number>> = {
  SKR03: {
    debtor: 10000, creditor: 70000,
    revenue_19: 8400, revenue_7: 8300, revenue_eu_rc: 8336, revenue_13b: 8337, revenue_abroad: 8338, revenue_small_business: 8195, revenue_other: 8200, revenue_late_costs: DATEV_DEFAULT_LATE_COSTS_ACCOUNT.SKR03, revenue_late_interest: DATEV_DEFAULT_LATE_INTEREST_ACCOUNT.SKR03,
    expense: 3100, expense_eu_rc: 3123, expense_abroad: 3125,
    bad_debt_19: 2406, bad_debt_7: 2401, bad_debt_other: 2400,
  },
  SKR04: {
    debtor: 10000, creditor: 70000,
    revenue_19: 4400, revenue_7: 4300, revenue_eu_rc: 4336, revenue_13b: 4337, revenue_abroad: 4338, revenue_small_business: 4185, revenue_other: 4200, revenue_late_costs: DATEV_DEFAULT_LATE_COSTS_ACCOUNT.SKR04, revenue_late_interest: DATEV_DEFAULT_LATE_INTEREST_ACCOUNT.SKR04,
    expense: 5900, expense_eu_rc: 5923, expense_abroad: 5925,
    bad_debt_19: 6936, bad_debt_7: 6931, bad_debt_other: 6930,
  },
};

export interface DatevConfig {
  /** Beraternummer (1001–9999999) und Mandantennummer (1–99999) aus der Kanzlei */
  advisorNumber: number;
  clientNumber: number;
  /** Beginn des Wirtschaftsjahres YYYY-MM-DD */
  fiscalYearStart: string;
  /** Sachkontenlänge 4–8 (Standard 4) */
  accountLength?: number;
  chart: DatevChart;
  accounts?: Partial<Record<DatevAccountKey, number>>;
  /** Kürzel der Herkunft (2 Zeichen) */
  origin?: string;
  /** Agentur ist Kleinunternehmer: keine Vorsteuer aus Creator-Rechnungen (Befund 24) */
  smallBusiness?: boolean;
}

export interface DatevInvoice {
  kind: "agency_to_brand" | "agency_to_talent" | "talent_to_agency" | "talent_to_brand";
  number: string;
  issueDate: string;
  serviceDate?: string | null;
  dueDate?: string | null;
  currency: string;
  grossCents: Cents;
  taxCents: Cents;
  netCents: Cents;
  /** Betrag in Euro, wenn die Rechnung in fremder Währung ist (eingefrorener Kurs) */
  grossEurCents?: Cents | null;
  /** K-308: Umsatzsteuer in Euro, wie auf dem PDF (0267/0269), mit Kurs (1 EUR = x Rechnungswährung), Tag und Quelle */
  taxEurCents?: Cents | null;
  taxEurRate?: number | null;
  taxEurRateOn?: string | null;
  taxEurSource?: string | null;
  taxMode: string | null;
  /** Steuersatz in Prozent (aus den Positionen), null = unbekannt */
  ratePercent: number | null;
  counterparty: string;
  counterpartyVatId?: string | null;
  counterpartyCountry?: string | null;
  selfBilled?: boolean;
  /** Status der Rechnung (export_invoices); Creator-Rechnungen zählen erst ab „approved“ */
  status?: string | null;
  /** Tag der Stornierung bzw. des Widerspruchs (Gegenbuchung einer Creator-Rechnung) */
  cancelledOn?: string | null;
  /** false: die Rechnung ist in einem früheren Zeitraum gebucht; hier nur Gegenbuchung bzw. Forderungsausfall */
  issuedInRange?: boolean;
  /** Forderungsausfall: Tag und ausgebuchter Betrag (brutto) */
  badDebtOn?: string | null;
  badDebtCents?: Cents | null;
  /** K-399 (0284): Anteil der Verzugszinsen an einem Beleg über Verzugskosten (Rechnungswährung, Vorzeichen wie gross), sonst null */
  lateInterestCents?: Cents | null;
}

/** Kaufmännisch auf ganze Cent runden, symmetrisch zum Nullpunkt wie PostgreSQL round(): −2,5 → −3 (Math.round ergäbe −2) */
export function roundHalfAway(x: number): number {
  const r = Math.round(Math.abs(x));
  return x < 0 && r !== 0 ? -r : r;
}

/** Steuer, die die DATEV-Automatik aus einem Bruttobetrag zieht: Brutto − Brutto/(1 + Satz), kaufmännisch gerundet (symmetrisch) */
export function datevAutomaticTax(grossCents: number, ratePercent: number): number {
  const pb = Math.round(ratePercent * 100);
  const d = 10_000 + pb;
  const b = Math.abs(grossCents);
  // ganzzahlig: round(b · pb / d) ohne Gleitkommarest
  const t = Math.floor((2 * b * pb + d) / (2 * d));
  return grossCents < 0 && t !== 0 ? -t : t;
}

/**
 * K-397: Bruttobetrag in Euro (Basisumsatz), aus dem die DATEV-Automatik genau `targetTaxCents` zieht, möglichst nah am
 * rechnerischen Brutto `naturalCents` (Netto in Euro + USt in Euro). Für 19 % und 7 % gibt es immer einen (exact = true); sonst das
 * nächstliegende Brutto mit der kleinsten Steuerabweichung (exact = false). Vorzeichen symmetrisch: Storno = −Original.
 */
export function datevBasisForTax(naturalCents: number, targetTaxCents: number, ratePercent: number): { cents: number; exact: boolean } {
  if (!(ratePercent > 0) || (naturalCents !== 0 && targetTaxCents !== 0 && Math.sign(naturalCents) !== Math.sign(targetTaxCents))) {
    return { cents: naturalCents, exact: datevAutomaticTax(naturalCents, ratePercent) === targetTaxCents };
  }
  const neg = naturalCents < 0 || (naturalCents === 0 && targetTaxCents < 0);
  const n = Math.abs(naturalCents), tt = Math.abs(targetTaxCents);
  const pb = Math.round(ratePercent * 100), d = 10_000 + pb;
  // round(B · pb / d) = T  ⇔  (2T − 1) · d ≤ 2 · B · pb < (2T + 1) · d
  const ceilDiv = (a: number, b: number) => (a <= 0 ? -Math.floor(-a / b) : Math.floor((a + b - 1) / b));
  const lo = Math.max(0, ceilDiv((2 * tt - 1) * d, 2 * pb));
  const hi = ceilDiv((2 * tt + 1) * d, 2 * pb) - 1;
  const sign = (c: number) => (neg && c !== 0 ? -c : c);
  if (lo <= hi) {
    const b = Math.min(Math.max(n, lo), hi);
    if (datevAutomaticTax(b, ratePercent) === tt) return { cents: sign(b), exact: true };
  }
  // kein Betrag passt genau: kleinste Steuerabweichung, dann der nächste am rechnerischen Brutto
  let best = n, bestDiff = Math.abs(datevAutomaticTax(n, ratePercent) - tt);
  for (let k = 1; k <= 100 && bestDiff > 0; k += 1) {
    for (const b of [n - k, n + k]) {
      if (b < 0) continue;
      const diff = Math.abs(datevAutomaticTax(b, ratePercent) - tt);
      if (diff < bestDiff) { best = b; bestDiff = diff; }
    }
  }
  return { cents: sign(best), exact: bestDiff === 0 };
}

/**
 * K-399: Beleg über Verzugskosten je Position aufteilen: Zinsen und der Rest (Mahngebühr, Pauschale). Euro-Beträge anteilig, der
 * Rest nimmt die Rundung auf (Summe bleibt). null, wenn es nichts aufzuteilen gibt (kein Zinsanteil oder unplausible Angabe).
 */
export function splitLateCosts(grossCents: number, grossEurCents: number | null, interestCents: number | null | undefined):
  { interest: { cents: number; eurCents: number | null }; rest: { cents: number; eurCents: number | null } } | null {
  const li = interestCents ?? 0;
  if (li === 0 || grossCents === 0 || Math.sign(li) !== Math.sign(grossCents) || Math.abs(li) > Math.abs(grossCents)) return null;
  const liEur = grossEurCents == null ? null : roundHalfAway((grossEurCents * li) / grossCents);
  return {
    interest: { cents: li, eurCents: liEur },
    rest: { cents: grossCents - li, eurCents: grossEurCents == null || liEur == null ? null : grossEurCents - liEur },
  };
}

/**
 * Euro-Beträge einer Rechnung in fremder Währung (K-308): mit eingefrorener Umsatzsteuer in Euro (PDF) gilt deren Kurs für die
 * ganze Rechnung (Netto umgerechnet plus die gedruckte Steuer), sonst der Euro-Betrag beim Versand (Steuer anteilig). Euro-Rechnungen
 * und Rechnungen ohne beides: null.
 */
export function datevEurAmounts(inv: Pick<DatevInvoice, "currency" | "grossCents" | "netCents" | "taxCents" | "grossEurCents" | "taxEurCents" | "taxEurRate">):
  { grossEurCents: Cents; taxEurCents: Cents; rate: number | null; fromPdf: boolean } | null {
  if (inv.currency === "EUR") return null;
  const rate = inv.taxEurRate == null ? NaN : Number(inv.taxEurRate);
  if (inv.taxEurCents != null && rate > 0) {
    // K-397: symmetrisch gerundet, damit das Storno spiegelgleich ist
    const netEur = roundHalfAway(inv.netCents / rate);
    return { grossEurCents: netEur + inv.taxEurCents, taxEurCents: inv.taxEurCents, rate, fromPdf: true };
  }
  if (inv.grossEurCents == null) return null;
  const tax = inv.grossCents !== 0 ? roundHalfAway((inv.taxCents * inv.grossEurCents) / inv.grossCents) : 0;
  return { grossEurCents: inv.grossEurCents, taxEurCents: tax, rate: null, fromPdf: false };
}

/** Belege, die die Agentur bucht: Creator-Rechnungen erst ab der Freigabe, keine Rechnungen eines Creators direkt an die Marke */
export function datevBooks(inv: Pick<DatevInvoice, "kind" | "status">): boolean {
  if (inv.kind === "talent_to_brand") return false;
  if (inv.kind === "talent_to_agency") return !inv.status || ["approved", "sent", "paid", "cancelled"].includes(inv.status);
  return !inv.status || !["draft", "submitted", "revision_requested", "rejected", "withdrawn", "discarded"].includes(inv.status);
}

export interface DatevProblem { code: "ADVISOR" | "CLIENT" | "FISCAL_YEAR" | "ACCOUNT_LENGTH" | "NO_ROWS" | "NO_EUR_AMOUNT"; number?: string }

/** Beginn des Wirtschaftsjahres, in dem `from` liegt (Monat aus den Einstellungen); null, wenn `to` in ein anderes Wirtschaftsjahr fällt */
export function datevFiscalYearStart(from: string, to: string, startMonth: number): string | null {
  const y = Number(from.slice(0, 4)) - (Number(from.slice(5, 7)) < startMonth ? 1 : 0);
  const start = `${y}-${String(startMonth).padStart(2, "0")}-01`;
  const end = `${y + 1}-${String(startMonth).padStart(2, "0")}-01`;
  return to >= start && to < end ? start : null;
}

export function checkDatevConfig(c: DatevConfig): DatevProblem[] {
  const out: DatevProblem[] = [];
  if (!Number.isInteger(c.advisorNumber) || c.advisorNumber < 1001 || c.advisorNumber > 9999999) out.push({ code: "ADVISOR" });
  if (!Number.isInteger(c.clientNumber) || c.clientNumber < 1 || c.clientNumber > 99999) out.push({ code: "CLIENT" });
  if (!/^\d{4}-\d{2}-01$/.test(c.fiscalYearStart)) out.push({ code: "FISCAL_YEAR" });
  const len = c.accountLength ?? 4;
  if (!Number.isInteger(len) || len < 4 || len > 8) out.push({ code: "ACCOUNT_LENGTH" });
  return out;
}

const q = (s: string, max: number) => `"${s.replace(/[\r\n;]+/g, " ").slice(0, max).replace(/"/g, '""')}"`;
/**
 * Belegfeld 1 nach DATEV-Format: nur a–z A–Z 0–9 $ & % * + - /, höchstens 36 Zeichen. Umlaute werden umschrieben,
 * andere Zeichen (Leerzeichen, Punkt, Unterstrich …) als „-“ zusammengefasst. Die Originalnummer bleibt im Buchungstext.
 */
export function datevBelegfeld1(number: string): string {
  return number
    .replace(/ß/g, "ss").replace(/Ä/g, "Ae").replace(/Ö/g, "Oe").replace(/Ü/g, "Ue").replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue")
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^A-Za-z0-9$&%*+\-/]+/g, "-")
    // GB-D2: vorn kein + oder - (in Excel „+A1“/„-A1“ eine Formel; ein Apostroph ist in Belegfeld 1 nicht zulässig)
    .replace(/^[-+]+|-+$/g, "")
    .slice(0, 36);
}
const amount = (c: Cents) => (Math.abs(c) / 100).toFixed(2).replace(".", ",");
const ddmm = (iso: string) => `${iso.slice(8, 10)}${iso.slice(5, 7)}`;
const ddmmyyyy = (iso: string) => `${iso.slice(8, 10)}${iso.slice(5, 7)}${iso.slice(0, 4)}`;
const ymd = (iso: string) => iso.slice(0, 10).replace(/-/g, "");

/** Erlös- bzw. Aufwandskonto und BU-Schlüssel je Steuerfall */
export function datevAccountsFor(inv: Pick<DatevInvoice, "kind" | "taxMode" | "ratePercent">, acc: Record<DatevAccountKey, number>, opts: { smallBusiness?: boolean } = {}): { account: number; bu: string | null } {
  const mode: TaxMode | null = taxModeFromDb(inv.taxMode);
  const rate = inv.ratePercent ?? 0;
  const is = (r: number) => Math.abs(rate - r) < 0.05;
  if (inv.kind === "talent_to_agency") {
    if (mode === "REVERSE_CHARGE_EU") return { account: acc.expense_eu_rc, bu: null };
    if (mode === "REVERSE_CHARGE_DOMESTIC" || mode === "NOT_TAXABLE_ABROAD") return { account: acc.expense_abroad, bu: null };
    // Vorsteuer über BU-Schlüssel: 9 = 19 %, 8 = 7 %; ohne Steuer (Kleinunternehmer) oder als Kleinunternehmer-Agentur kein Schlüssel
    if (opts.smallBusiness) return { account: acc.expense, bu: null };
    return { account: acc.expense, bu: mode === "DOMESTIC" && is(19) ? "9" : mode === "DOMESTIC" && is(7) ? "8" : null };
  }
  switch (mode) {
    // Nur die deutschen Sätze haben Automatikkonten; andere Sätze (z. B. 20 %) auf „Sonstige Erlöse“ zur Prüfung
    case "DOMESTIC": return { account: is(19) ? acc.revenue_19 : is(7) ? acc.revenue_7 : acc.revenue_other, bu: null };
    case "REVERSE_CHARGE_EU": return { account: acc.revenue_eu_rc, bu: null };
    case "REVERSE_CHARGE_DOMESTIC": return { account: acc.revenue_13b, bu: null };
    case "NOT_TAXABLE_ABROAD": return { account: acc.revenue_abroad, bu: null };
    case "SMALL_BUSINESS": return { account: acc.revenue_small_business, bu: null };
    // K-313: Verzugskosten sind Schadensersatz, kein Umsatzerlös
    case "NOT_TAXABLE_DAMAGES": return { account: acc.revenue_late_costs, bu: null };
    default: return { account: acc.revenue_other, bu: null };
  }
}

/**
 * Buchungsstapel als Text (Zeilenende CRLF). Beträge immer positiv, Richtung über Soll/Haben; Fremdwährung mit Euro-Basisbetrag.
 * Rechnungen eines Creators direkt an die Marke werden übersprungen (kein Beleg der Agentur).
 */
export function buildDatevBuchungsstapel(input: {
  config: DatevConfig;
  invoices: readonly DatevInvoice[];
  from: string;
  to: string;
  createdAt: Date;
  label?: string;
  exportedBy?: string;
}): { text: string; rows: number; skipped: number; problems: DatevProblem[] } {
  const { config: c } = input;
  const problems = checkDatevConfig(c);
  const acc = { ...DATEV_DEFAULT_ACCOUNTS[c.chart], ...(c.accounts ?? {}) } as Record<DatevAccountKey, number>;
  const created = input.createdAt.toISOString().replace(/[-:TZ.]/g, "").slice(0, 17).padEnd(17, "0");
  const header = [
    '"EXTF"', "700", "21", '"Buchungsstapel"', "13", created, "", q((c.origin ?? "TK").slice(0, 2), 2), q(input.exportedBy ?? "CreatorOffice", 25), "",
    String(c.advisorNumber), String(c.clientNumber), ymd(c.fiscalYearStart), String(c.accountLength ?? 4), ymd(input.from), ymd(input.to),
    q(input.label ?? "CreatorOffice Rechnungen", 30), "", "1", "", "0", '"EUR"', "", "", "", "", "", "", "", "", "",
  ];
  const lines: string[] = [header.join(";"), DATEV_COLUMNS.join(";")];
  let skipped = 0;
  const inRange = (d: string | null | undefined) => !!d && d >= input.from && d <= input.to;
  for (const inv of input.invoices) {
    if (!datevBooks(inv)) { skipped += 1; continue; }
    const incoming = inv.kind === "talent_to_agency";
    const { account, bu } = datevAccountsFor(inv, acc, { smallBusiness: c.smallBusiness });
    const foreign = inv.currency !== "EUR";
    const eur = datevEurAmounts(inv);
    if (foreign && !eur) problems.push({ code: "NO_EUR_AMOUNT", number: inv.number });
    const row = (opts: { cents: number; eurCents: number | null | undefined; sh: "S" | "H"; konto: number; gegen: number; bu: string | null; date: string; text: string; reverse?: boolean; rate?: number | null; info?: string | null }) => {
      const f: string[] = new Array(DATEV_COLUMNS.length).fill("");
      f[0] = amount(opts.cents);
      f[1] = `"${opts.sh}"`;
      if (foreign) {
        f[2] = q(inv.currency, 3);
        if (opts.eurCents != null && opts.cents !== 0) {
          f[3] = (opts.rate ?? Math.abs(opts.cents) / Math.abs(opts.eurCents)).toFixed(6).replace(".", ",");
          f[4] = amount(opts.eurCents);
          f[5] = '"EUR"';
        }
      }
      f[6] = String(opts.konto);
      f[7] = String(opts.gegen);
      f[8] = opts.bu ? `"${opts.bu}"` : "";
      f[9] = ddmm(opts.date);
      const beleg = datevBelegfeld1(inv.number);
      f[10] = q(beleg, 36);
      // Musste die Nummer für Belegfeld 1 angepasst werden, steht die Originalnummer vorn im Buchungstext
      // GB-D2: Formelschutz wie BMD (Name der Gegenpartei bzw. frei getippte Creator-Nummer vorn; wird die Datei in Excel geöffnet)
      f[13] = q(csvFormulaGuard(beleg === inv.number ? opts.text : `${inv.number} ${opts.text}`), 60);
      // K-308: Umsatzsteuer in Euro wie auf dem PDF (Betrag, Kurs, Tag, Quelle) als Beleginfo
      if (opts.info) { f[20] = q("USt in EUR", 20); f[21] = q(csvFormulaGuard(opts.info), 210); }
      if (!incoming && taxModeFromDb(inv.taxMode) === "REVERSE_CHARGE_EU" && inv.counterpartyVatId) f[39] = q(inv.counterpartyVatId.replace(/\s+/g, "").toUpperCase(), 15);
      if (inv.serviceDate) f[114] = ddmmyyyy(inv.serviceDate);
      if (inv.dueDate) f[116] = ddmmyyyy(inv.dueDate);
      if (opts.reverse) f[117] = '"1"';
      lines.push(f.join(";"));
    };
    const negative = inv.grossCents < 0;
    // Ausgangsrechnung: Debitor im Soll; Eingangsrechnung/Gutschrift: Kreditor im Haben; Storno dreht die Richtung
    const base = incoming ? "H" : "S";
    const sh: "S" | "H" = negative ? (base === "S" ? "H" : "S") : base;
    const konto = incoming ? acc.creditor : acc.debtor;
    const label = `${inv.selfBilled ? "Gutschrift " : ""}${inv.counterparty}`;
    // K-396 (0298): beim BMF-Monatskurs nennt die Quelle den Monat; ein Kurstag („vom 01.09.“) wäre irreführend
    const info = eur?.fromPdf
      ? `${amount(eur.taxEurCents)} EUR · Kurs 1 EUR = ${String(eur.rate).replace(".", ",")} ${inv.currency}${inv.taxEurRateOn && !/^BMF-Umrechnungskurs /.test(inv.taxEurSource ?? "") ? ` vom ${inv.taxEurRateOn.slice(8, 10)}.${inv.taxEurRateOn.slice(5, 7)}.${inv.taxEurRateOn.slice(0, 4)}` : ""}${inv.taxEurSource ? ` (${inv.taxEurSource})` : ""}`
      : null;
    // K-397: Satz der DATEV-Automatik dieser Buchung (Erlöse 19 %/7 %, Vorsteuer BU 9/8), sonst keine Automatik
    const mode = taxModeFromDb(inv.taxMode);
    const rateOf = (r: number | null) => (r != null && Math.abs(r - 19) < 0.05 ? 19 : r != null && Math.abs(r - 7) < 0.05 ? 7 : null);
    const autoRate = incoming ? (bu === "9" ? 19 : bu === "8" ? 7 : null) : mode === "DOMESTIC" ? rateOf(inv.ratePercent) : null;
    // Basisumsatz so, dass die Automatik genau die USt in Euro des PDF zieht
    const basis = eur?.fromPdf && autoRate ? datevBasisForTax(eur.grossEurCents, eur.taxEurCents, autoRate).cents : eur?.grossEurCents ?? null;
    // K-399: Beleg über Verzugskosten je Position (Zinsen auf das Zinskonto, Gebühr und Pauschale auf das Verzugskostenkonto)
    const split = mode === "NOT_TAXABLE_DAMAGES" ? splitLateCosts(inv.grossCents, basis, inv.lateInterestCents) : null;
    const parts: { cents: number; eurCents: number | null; gegen: number; text: string }[] = split
      ? [
          { ...split.interest, gegen: acc.revenue_late_interest, text: `Verzugszinsen ${label}` },
          ...(split.rest.cents !== 0 ? [{ ...split.rest, gegen: account, text: `Verzugskosten ${label}` }] : []),
        ]
      : [{ cents: inv.grossCents, eurCents: basis, gegen: account, text: label }];
    const rate = eur?.rate ?? null;
    if (inv.issuedInRange !== false) {
      for (const p of parts) row({ cents: p.cents, eurCents: p.eurCents, rate, info, sh, konto, gegen: p.gegen, bu, date: inv.issueDate, text: p.text });
    }
    // Stornierte Creator-Rechnung bzw. widersprochene Gutschrift: Gegenbuchung (Generalumkehr) am Tag der Stornierung
    if (incoming && inv.status === "cancelled" && inRange(inv.cancelledOn)) {
      for (const p of parts) row({ cents: p.cents, eurCents: p.eurCents, rate, info, sh, konto, gegen: p.gegen, bu, date: inv.cancelledOn!, text: `Storno ${p.text}`, reverse: true });
    }
    // Forderungsausfall: Debitor im Haben gegen Forderungsverluste (Automatikkonto korrigiert die Umsatzsteuer)
    if (!incoming && inRange(inv.badDebtOn) && (inv.badDebtCents ?? 0) > 0) {
      const lossRate = mode === "DOMESTIC" ? rateOf(inv.ratePercent) : null;
      const lossAccount = lossRate === 19 ? acc.bad_debt_19 : lossRate === 7 ? acc.bad_debt_7 : acc.bad_debt_other;
      let badEur = eur && inv.grossCents !== 0 ? roundHalfAway((inv.badDebtCents! * eur.grossEurCents) / inv.grossCents) : null;
      // K-397: die Automatik der Forderungsverluste berichtigt anteilig genau die USt in Euro des PDF (voller Ausfall: wie gebucht)
      if (badEur != null && eur?.fromPdf && lossRate) {
        const badTax = roundHalfAway((inv.badDebtCents! * eur.taxEurCents) / inv.grossCents);
        badEur = datevBasisForTax(badEur, badTax, lossRate).cents;
      }
      row({ cents: inv.badDebtCents!, eurCents: badEur, rate, sh: "H", konto: acc.debtor, gegen: lossAccount, bu: null, date: inv.badDebtOn!, text: `Forderungsausfall ${inv.counterparty}` });
    }
  }
  return { text: `${lines.join("\r\n")}\r\n`, rows: lines.length - 2, skipped, problems };
}
